import React, { useState, useMemo } from 'react';
import { 
    Box, Grid, Card, CardContent, Typography, Table, TableBody, TableCell, 
    TableContainer, TableHead, TableRow, Paper, Chip, Button, TextField, 
    MenuItem, Select, FormControl, InputLabel, Divider, Dialog, DialogTitle, 
    DialogContent, DialogActions, Tabs, Tab 
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import PaymentsIcon from '@mui/icons-material/Payments';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import DiscountIcon from '@mui/icons-material/Discount';
import PrintIcon from '@mui/icons-material/Print';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import BarChartIcon from '@mui/icons-material/BarChart';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import CategoryIcon from '@mui/icons-material/Category';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title as ChartTitle, Tooltip, Legend } from 'chart.js';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';

// Register ChartJS modules
ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTitle, Tooltip, Legend);

const getEffectiveBusinessDate = (dateInput = new Date(), cutoffHour = 4) => {
    try {
        const d = (dateInput instanceof Date) ? new Date(dateInput.getTime()) : new Date(dateInput);
        if (isNaN(d.getTime())) return new Date().toISOString().split('T')[0];
        d.setHours(d.getHours() - cutoffHour);
        return d.toISOString().split('T')[0];
    } catch (e) {
        return new Date().toISOString().split('T')[0];
    }
};

export default function SettingsView() {
    const { orders, menuItems, categories: appCategories } = useApp();
    const { t, currentLanguage } = useLanguage();

    // Active Tab state: 'daily' (Report 1) or 'itemWise' (Report 2)
    const [reportTab, setReportTab] = useState('daily');

    // Common Filter states
    const [dateFilter, setDateFilter] = useState('today'); // 'today', 'yesterday', 'week', 'month', 'custom'
    const [fromDate, setFromDate] = useState(() => getEffectiveBusinessDate());
    const [toDate, setToDate] = useState(() => getEffectiveBusinessDate());
    const [paymentFilter, setPaymentFilter] = useState('all'); // 'all', 'cash', 'upi', 'split'
    const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('all');

    // On-Demand Chart toggle state (Default FALSE for 0% background overhead)
    const [showChart, setShowChart] = useState(false);

    // Closing Receipt Preview Dialog state
    const [openClosingDialog, setOpenClosingDialog] = useState(false);

    // Filtered orders calculation based on selected Date Range & Payment Method
    const filteredOrders = useMemo(() => {
        const todayBusinessStr = getEffectiveBusinessDate(new Date(), 4);
        
        const list = orders.filter(o => {
            const orderDateStr = o.businessDate || o.business_date || (o.createdAt ? getEffectiveBusinessDate(o.createdAt, 4) : todayBusinessStr);

            // Date filtering
            let dateMatch = true;
            if (dateFilter === 'today') {
                dateMatch = (orderDateStr === todayBusinessStr);
            } else if (dateFilter === 'yesterday') {
                const yest = new Date(todayBusinessStr);
                yest.setDate(yest.getDate() - 1);
                const yestStr = yest.toISOString().split('T')[0];
                dateMatch = (orderDateStr === yestStr);
            } else if (dateFilter === 'week') {
                const weekAgo = new Date(todayBusinessStr);
                weekAgo.setDate(weekAgo.getDate() - 7);
                dateMatch = (orderDateStr >= weekAgo.toISOString().split('T')[0] && orderDateStr <= todayBusinessStr);
            } else if (dateFilter === 'month') {
                const monthAgo = new Date(todayBusinessStr);
                monthAgo.setDate(monthAgo.getDate() - 30);
                dateMatch = (orderDateStr >= monthAgo.toISOString().split('T')[0] && orderDateStr <= todayBusinessStr);
            } else if (dateFilter === 'custom') {
                dateMatch = (orderDateStr >= fromDate && orderDateStr <= toDate);
            }

            // Payment method filtering
            let payMatch = true;
            if (paymentFilter !== 'all') {
                const method = (o.paymentMethod || '').toLowerCase();
                payMatch = method.includes(paymentFilter.toLowerCase());
            }

            return dateMatch && payMatch;
        });

        // Chronological Ascending Sorting (First bill of the day at the top)
        list.sort((a, b) => {
            const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            if (timeA !== timeB) return timeA - timeB;
            return String(a.id).localeCompare(String(b.id), undefined, { numeric: true, sensitivity: 'base' });
        });

        return list;
    }, [orders, dateFilter, fromDate, toDate, paymentFilter]);

    // Financial Metrics Calculation for Report 1
    const totalSales = useMemo(() => filteredOrders.reduce((sum, o) => sum + (Number(o.total) || 0), 0), [filteredOrders]);
    const totalDiscount = useMemo(() => filteredOrders.reduce((sum, o) => sum + (Number(o.discount) || 0), 0), [filteredOrders]);
    const totalTax = useMemo(() => filteredOrders.reduce((sum, o) => sum + (Number(o.tax) || 0), 0), [filteredOrders]);
    const totalOrdersCount = filteredOrders.length;

    const cashSales = useMemo(() => {
        return filteredOrders.reduce((sum, o) => {
            const method = (o.paymentMethod || '').toLowerCase();
            if (method === 'cash') return sum + o.total;
            if (method.includes('split')) {
                const match = method.match(/cash:\s*₹?(\d+(\.\d+)?)/i);
                if (match) return sum + parseFloat(match[1]);
            }
            return sum;
        }, 0);
    }, [filteredOrders]);

    const upiSales = useMemo(() => {
        return filteredOrders.reduce((sum, o) => {
            const method = (o.paymentMethod || '').toLowerCase();
            if (method.includes('upi') || method.includes('qr') || method.includes('card')) return sum + o.total;
            if (method.includes('split')) {
                const match = method.match(/upi:\s*₹?(\d+(\.\d+)?)/i);
                if (match) return sum + parseFloat(match[1]);
            }
            return sum;
        }, 0);
    }, [filteredOrders]);

    // Item-Wise Sales Aggregation for Report 2
    const itemSalesData = useMemo(() => {
        const itemMap = {};

        filteredOrders.forEach(o => {
            if (o.items && Array.isArray(o.items)) {
                o.items.forEach(item => {
                    const id = item.id || item.menu_item_id || item.name;
                    const name = item.name;
                    const nameMr = item.nameMr || item.name;
                    const category = item.category || 'General';
                    const price = item.price || 0;
                    const qty = item.quantity || 1;

                    // Category filter
                    if (selectedCategoryFilter !== 'all' && category.toLowerCase() !== selectedCategoryFilter.toLowerCase()) {
                        return;
                    }

                    if (!itemMap[id]) {
                        itemMap[id] = {
                            id,
                            name,
                            nameMr,
                            category,
                            price,
                            totalQty: 0,
                            totalRevenue: 0
                        };
                    }

                    itemMap[id].totalQty += qty;
                    itemMap[id].totalRevenue += (qty * price);
                });
            }
        });

        const list = Object.values(itemMap);
        list.sort((a, b) => b.totalQty - a.totalQty); // Sort descending by Qty Sold
        return list;
    }, [filteredOrders, selectedCategoryFilter]);

    // Item-Wise Overall Metrics
    const totalItemsSoldQty = useMemo(() => itemSalesData.reduce((sum, i) => sum + i.totalQty, 0), [itemSalesData]);
    const totalItemRevenue = useMemo(() => itemSalesData.reduce((sum, i) => sum + i.totalRevenue, 0), [itemSalesData]);
    const topSellingDish = itemSalesData.length > 0 ? itemSalesData[0] : null;

    // Top 10 Best Selling Items for On-Demand Chart
    const chartData = useMemo(() => {
        const top10 = itemSalesData.slice(0, 10);
        return {
            labels: top10.map(i => currentLanguage === 'mr' ? i.nameMr : i.name),
            datasets: [
                {
                    label: 'विकलेले नग (Quantity Sold)',
                    data: top10.map(i => i.totalQty),
                    backgroundColor: '#ea580c',
                    borderRadius: 8
                }
            ]
        };
    }, [itemSalesData, currentLanguage]);

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            title: { display: true, text: 'Top 10 Best Selling Items (सर्वाधिक खपणारे पदार्थ)', font: { size: 14, weight: 'bold' } }
        },
        scales: {
            y: { beginAtZero: true, grid: { color: '#f1f5f9' } },
            x: { grid: { display: false } }
        }
    };

    // Export Report 1 to CSV
    const handleExportDailyCSV = () => {
        if (filteredOrders.length === 0) {
            alert('No data available to export.');
            return;
        }

        let csv = 'Sr. No.,Order ID,Date & Time,Type,Waiter,Subtotal,Discount,GST Tax,Grand Total,Payment Method,Status\n';
        filteredOrders.forEach((o, index) => {
            const time = o.createdAt ? new Date(o.createdAt).toLocaleString('en-IN') : '';
            csv += `${index + 1},"${o.id}","${time}","${o.type || ''}","${o.waiterName || ''}",${o.subtotal || 0},${o.discount || 0},${o.tax || 0},${o.total || 0},"${o.paymentMethod || 'Cash'}","${o.status || 'Settled'}"\n`;
        });

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Dinexa_Daily_Sales_Report_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Export Report 2 to CSV
    const handleExportItemWiseCSV = () => {
        if (itemSalesData.length === 0) {
            alert('No item sales data available to export.');
            return;
        }

        let csv = 'Dish Name,Category,Unit Price (Rs),Quantity Sold,Total Revenue (Rs),Sales Share (%)\n';
        itemSalesData.forEach(i => {
            const share = totalItemRevenue > 0 ? ((i.totalRevenue / totalItemRevenue) * 100).toFixed(1) : '0';
            csv += `"${i.name}","${i.category}",${i.price},${i.totalQty},${i.totalRevenue},"${share}%"\n`;
        });

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Dinexa_ItemWise_Sales_Report_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Print Daily Closing Receipt
    const handlePrintDailyClosing = async () => {
        try {
            window.print();
        } catch (e) {
            console.error("Print error:", e);
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f8fafc', minHeight: 'calc(100vh - 70px)' }}>
            {/* Header Title Banner */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 900, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <ReceiptLongIcon sx={{ color: '#ea580c', fontSize: 32 }} />
                        {currentLanguage === 'mr' ? 'अहवाल आणि विश्लेषण (Reports & Analytics)' : 'SaaS Sales & Transaction Reports'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mt: 0.5 }}>
                        {currentLanguage === 'mr' ? 'दैनिक विक्री, जमा रक्कम, आणि पदार्थनिहाय खपाचा सविस्तर अहवाल' : 'Comprehensive daily revenue tally and item-wise sales performance analytics'}
                    </Typography>
                </Box>
            </Box>

            {/* Navigation Tabs between Report 1 and Report 2 */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 3, borderBottom: '2px solid #e2e8f0', pb: 1 }}>
                <Button
                    variant={reportTab === 'daily' ? 'contained' : 'outlined'}
                    onClick={() => setReportTab('daily')}
                    startIcon={<ReceiptLongIcon />}
                    sx={{
                        fontWeight: 900,
                        borderRadius: 2.5,
                        py: 1,
                        px: 2.5,
                        bgcolor: reportTab === 'daily' ? '#ea580c' : 'transparent',
                        borderColor: '#ea580c',
                        color: reportTab === 'daily' ? '#ffffff' : '#ea580c',
                        '&:hover': { bgcolor: reportTab === 'daily' ? '#c2410c' : '#fff7ed' },
                        textTransform: 'none'
                    }}
                >
                    १. दैनिक विक्री अहवाल (Daily Sales Report)
                </Button>

                <Button
                    variant={reportTab === 'itemWise' ? 'contained' : 'outlined'}
                    onClick={() => setReportTab('itemWise')}
                    startIcon={<RestaurantMenuIcon />}
                    sx={{
                        fontWeight: 900,
                        borderRadius: 2.5,
                        py: 1,
                        px: 2.5,
                        bgcolor: reportTab === 'itemWise' ? '#ea580c' : 'transparent',
                        borderColor: '#ea580c',
                        color: reportTab === 'itemWise' ? '#ffffff' : '#ea580c',
                        '&:hover': { bgcolor: reportTab === 'itemWise' ? '#c2410c' : '#fff7ed' },
                        textTransform: 'none'
                    }}
                >
                    २. पदार्थनिहाय विक्री अहवाल (Item-Wise Sales Report)
                </Button>
            </Box>

            {/* Common Filter Selector Bar */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 3, borderColor: '#e2e8f0', bgcolor: '#ffffff' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                        <FormControl size="small" fullWidth>
                            <InputLabel sx={{ fontWeight: 700 }}>तारीख निवडा (Date Filter)</InputLabel>
                            <Select
                                value={dateFilter}
                                label="तारीख निवडा (Date Filter)"
                                onChange={(e) => setDateFilter(e.target.value)}
                                sx={{ fontWeight: 800, borderRadius: 2 }}
                            >
                                <MenuItem value="today">आज (Today)</MenuItem>
                                <MenuItem value="yesterday">काल (Yesterday)</MenuItem>
                                <MenuItem value="week">चालू आठवडा (This Week)</MenuItem>
                                <MenuItem value="month">चालू महिना (This Month)</MenuItem>
                                <MenuItem value="custom">कस्टम रेंज (Custom Range)</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {dateFilter === 'custom' && (
                        <>
                            <Grid item xs={6} sm={2.5}>
                                <TextField
                                    type="date"
                                    size="small"
                                    fullWidth
                                    label="From Date"
                                    InputLabelProps={{ shrink: true }}
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={6} sm={2.5}>
                                <TextField
                                    type="date"
                                    size="small"
                                    fullWidth
                                    label="To Date"
                                    InputLabelProps={{ shrink: true }}
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                />
                            </Grid>
                        </>
                    )}

                    {reportTab === 'daily' ? (
                        <Grid item xs={12} sm={3}>
                            <FormControl size="small" fullWidth>
                                <InputLabel sx={{ fontWeight: 700 }}>पेमेंट प्रकार (Payment Mode)</InputLabel>
                                <Select
                                    value={paymentFilter}
                                    label="पेमेंट प्रकार (Payment Mode)"
                                    onChange={(e) => setPaymentFilter(e.target.value)}
                                    sx={{ fontWeight: 800, borderRadius: 2 }}
                                >
                                    <MenuItem value="all">सर्व प्रकार (All Payment Modes)</MenuItem>
                                    <MenuItem value="cash">फक्त रोख (Cash Only)</MenuItem>
                                    <MenuItem value="upi">फक्त ऑनलाईन (UPI / QR Only)</MenuItem>
                                    <MenuItem value="split">स्प्लिट बिल (Split Payments)</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    ) : (
                        <Grid item xs={12} sm={3}>
                            <FormControl size="small" fullWidth>
                                <InputLabel sx={{ fontWeight: 700 }}>कॅटेगिरी निवडा (Category)</InputLabel>
                                <Select
                                    value={selectedCategoryFilter}
                                    label="कॅटेगिरी निवडा (Category)"
                                    onChange={(e) => setSelectedCategoryFilter(e.target.value)}
                                    sx={{ fontWeight: 800, borderRadius: 2 }}
                                >
                                    <MenuItem value="all">सर्व कॅटेगिरी (All Categories)</MenuItem>
                                    {appCategories.map(c => (
                                        <MenuItem key={c.id} value={c.name}>{currentLanguage === 'mr' ? (c.name_mr || c.name) : c.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    )}
                </Grid>
            </Paper>

            {/* REPORT 1: Daily Sales & Summary */}
            {reportTab === 'daily' && (
                <Box>
                    {/* Action Bar */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mb: 2 }}>
                        <Button
                            variant="outlined"
                            startIcon={<FileDownloadIcon />}
                            onClick={handleExportDailyCSV}
                            sx={{ borderColor: '#cbd5e1', color: '#334155', fontWeight: 800, borderRadius: 2.5, textTransform: 'none' }}
                        >
                            Export CSV
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<PrintIcon />}
                            onClick={() => setOpenClosingDialog(true)}
                            sx={{ bgcolor: '#ea580c', '&:hover': { bgcolor: '#c2410c' }, fontWeight: 900, borderRadius: 2.5, textTransform: 'none', boxShadow: '0 4px 12px rgba(234, 88, 12, 0.3)' }}
                        >
                            🖨️ Daily Closing Print
                        </Button>
                    </Box>

                    {/* 5 Key Financial Summary Cards */}
                    <Grid container spacing={2.5} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <Card sx={{ borderRadius: 3, border: '2px solid #10b981', bgcolor: '#ecfdf5', boxShadow: 'none' }}>
                                <CardContent sx={{ p: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#047857' }}>
                                            एकूण विक्री (TOTAL REVENUE)
                                        </Typography>
                                        <CurrencyRupeeIcon sx={{ color: '#10b981', fontSize: 22 }} />
                                    </Box>
                                    <Typography variant="h5" sx={{ fontWeight: 900, color: '#065f46' }}>
                                        ₹{totalSales.toFixed(0)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={2.4}>
                            <Card sx={{ borderRadius: 3, border: '2px solid #16a34a', bgcolor: '#f0fdf4', boxShadow: 'none' }}>
                                <CardContent sx={{ p: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#15803d' }}>
                                            रोख जमा (CASH COLLECTION)
                                        </Typography>
                                        <PaymentsIcon sx={{ color: '#16a34a', fontSize: 22 }} />
                                    </Box>
                                    <Typography variant="h5" sx={{ fontWeight: 900, color: '#14532d' }}>
                                        ₹{cashSales.toFixed(0)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={2.4}>
                            <Card sx={{ borderRadius: 3, border: '2px solid #0284c7', bgcolor: '#f0f9ff', boxShadow: 'none' }}>
                                <CardContent sx={{ p: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#0369a1' }}>
                                            ऑनलाईन जमा (UPI / ONLINE)
                                        </Typography>
                                        <QrCode2Icon sx={{ color: '#0284c7', fontSize: 22 }} />
                                    </Box>
                                    <Typography variant="h5" sx={{ fontWeight: 900, color: '#0c4a6e' }}>
                                        ₹{upiSales.toFixed(0)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={2.4}>
                            <Card sx={{ borderRadius: 3, border: '1px solid #cbd5e1', bgcolor: '#ffffff', boxShadow: 'none' }}>
                                <CardContent sx={{ p: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#475569' }}>
                                            एकूण ऑर्डर्स (TOTAL BILLS)
                                        </Typography>
                                        <ShoppingBagIcon sx={{ color: '#64748b', fontSize: 22 }} />
                                    </Box>
                                    <Typography variant="h5" sx={{ fontWeight: 900, color: '#0f172a' }}>
                                        {totalOrdersCount} <Typography component="span" variant="caption" sx={{ fontWeight: 700, color: '#64748b' }}>BILLS</Typography>
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={2.4}>
                            <Card sx={{ borderRadius: 3, border: '1px solid #fdba74', bgcolor: '#fff7ed', boxShadow: 'none' }}>
                                <CardContent sx={{ p: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#c2410c' }}>
                                            दिलेली सवलत (DISCOUNTS)
                                        </Typography>
                                        <DiscountIcon sx={{ color: '#ea580c', fontSize: 22 }} />
                                    </Box>
                                    <Typography variant="h5" sx={{ fontWeight: 900, color: '#9a3412' }}>
                                        -₹{totalDiscount.toFixed(0)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Transactions Settlement Table */}
                    <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 900, color: '#0f172a', mb: 2 }}>
                                सविस्तर व्यवहार रजिस्टर (Detailed Transactions Ledger)
                            </Typography>

                            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2.5 }}>
                                <Table size="medium">
                                    <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 900, width: 85, textAlign: 'center' }}>दैनिक बिल क्र. (#)</TableCell>
                                            <TableCell sx={{ fontWeight: 900 }}>{t('rep-col-id')}</TableCell>
                                            <TableCell sx={{ fontWeight: 900 }}>वेळ (Time)</TableCell>
                                            <TableCell sx={{ fontWeight: 900 }}>{t('rep-col-table')}</TableCell>
                                            <TableCell sx={{ fontWeight: 900 }}>वेटर्स (Waiter)</TableCell>
                                            <TableCell sx={{ fontWeight: 900 }} align="right">{t('rep-col-sub')}</TableCell>
                                            <TableCell sx={{ fontWeight: 900 }} align="right">{t('rep-col-disc')}</TableCell>
                                            <TableCell sx={{ fontWeight: 900 }} align="right">{t('rep-col-tax')}</TableCell>
                                            <TableCell sx={{ fontWeight: 900 }} align="right">{t('rep-col-total')}</TableCell>
                                            <TableCell sx={{ fontWeight: 900 }}>पेमेंट प्रकार (Payment)</TableCell>
                                            <TableCell sx={{ fontWeight: 900 }}>{t('rep-col-status')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredOrders.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={11} align="center" sx={{ py: 6, color: '#94a3b8', fontWeight: 600 }}>
                                                    निवडलेल्या फिल्टरसाठी कोणतेही व्यवहार सापडले नाहीत. (No transactions found)
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            filteredOrders.map((o, index) => {
                                                const pMethod = (o.paymentMethod || 'cash').toLowerCase();
                                                const isCash = pMethod === 'cash';
                                                const isUpi = pMethod.includes('upi') || pMethod.includes('qr');
                                                const isSplit = pMethod.includes('split');

                                                return (
                                                    <TableRow key={o.id} hover>
                                                        <TableCell align="center">
                                                            <Chip label={`#${index + 1}`} size="small" sx={{ fontWeight: 900, bgcolor: '#f1f5f9', color: '#0f172a', border: '1px solid #cbd5e1' }} />
                                                        </TableCell>
                                                        <TableCell sx={{ fontWeight: 800, color: '#0f172a' }}>{o.id}</TableCell>
                                                        <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: '0.8rem' }}>
                                                            {o.createdAt ? new Date(o.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '-'}
                                                        </TableCell>
                                                        <TableCell sx={{ fontWeight: 700 }}>{o.type}</TableCell>
                                                        <TableCell sx={{ fontWeight: 600, color: '#475569' }}>{o.waiterName || 'Counter Staff'}</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 700 }}>₹{o.subtotal.toFixed(0)}</TableCell>
                                                        <TableCell align="right" sx={{ color: '#ea580c', fontWeight: 700 }}>
                                                            {o.discount > 0 ? `-₹${o.discount.toFixed(0)}` : '-'}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 700 }}>₹{o.tax.toFixed(0)}</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 900, color: '#10b981', fontSize: '0.95rem' }}>₹{o.total.toFixed(0)}</TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={isCash ? 'CASH (रोख)' : (isUpi ? 'UPI (ऑनलाइन)' : (isSplit ? 'SPLIT (विभागून)' : o.paymentMethod))}
                                                                size="small"
                                                                sx={{
                                                                    bgcolor: isCash ? '#f0fdf4' : (isUpi ? '#f0f9ff' : '#fff7ed'),
                                                                    color: isCash ? '#16a34a' : (isUpi ? '#0284c7' : '#ea580c'),
                                                                    fontWeight: 900,
                                                                    border: '1px solid',
                                                                    borderColor: isCash ? '#bbf7d0' : (isUpi ? '#bae6fd' : '#fed7aa'),
                                                                    fontSize: '0.7rem'
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={o.status === 'served' ? t('rep-status-paid') : (o.status === 'ready' ? t('rep-status-ready') : t('rep-status-preparing'))}
                                                                size="small"
                                                                color={o.status === 'served' ? 'success' : (o.status === 'ready' ? 'warning' : 'error')}
                                                                sx={{ fontWeight: 900, fontSize: '0.65rem', height: 20 }}
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Box>
            )}

            {/* REPORT 2: Item-Wise Dish Sales Report */}
            {reportTab === 'itemWise' && (
                <Box>
                    {/* Action Bar with On-Demand Chart Toggle & Export */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                        {/* On-Demand Chart Toggle Button (Strictly runs only on click) */}
                        <Button
                            variant={showChart ? 'contained' : 'outlined'}
                            onClick={() => setShowChart(!showChart)}
                            startIcon={<BarChartIcon />}
                            endIcon={showChart ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            sx={{
                                fontWeight: 900,
                                borderRadius: 2.5,
                                py: 1,
                                px: 2.5,
                                bgcolor: showChart ? '#ea580c' : 'transparent',
                                borderColor: '#ea580c',
                                color: showChart ? '#ffffff' : '#ea580c',
                                '&:hover': { bgcolor: showChart ? '#c2410c' : '#fff7ed' },
                                textTransform: 'none'
                            }}
                        >
                            {showChart ? '📊 विश्लेषक चार्ट लपवा (Hide Chart)' : '📊 Show Sales Chart (चार्ट विश्लेषक उघडा)'}
                        </Button>

                        <Button
                            variant="outlined"
                            startIcon={<FileDownloadIcon />}
                            onClick={handleExportItemWiseCSV}
                            sx={{ borderColor: '#cbd5e1', color: '#334155', fontWeight: 800, borderRadius: 2.5, textTransform: 'none' }}
                        >
                            Export Item Sales CSV
                        </Button>
                    </Box>

                    {/* On-Demand Interactive Chart Section (Rendered ONLY when showChart is TRUE) */}
                    {showChart && (
                        <Card sx={{ mb: 3, borderRadius: 3, border: '2px solid #ea580c', boxShadow: '0 10px 25px -5px rgba(234, 88, 12, 0.15)' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#0f172a', mb: 2 }}>
                                    📊 Top 10 Best Selling Dishes Analytics Chart
                                </Typography>
                                <Box sx={{ height: 320 }}>
                                    <Bar data={chartData} options={chartOptions} />
                                </Box>
                            </CardContent>
                        </Card>
                    )}

                    {/* 3 Summary Cards for Item Sales */}
                    <Grid container spacing={2.5} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={4}>
                            <Card sx={{ borderRadius: 3, border: '2px solid #ea580c', bgcolor: '#fff7ed', boxShadow: 'none' }}>
                                <CardContent sx={{ p: 2.5 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#c2410c' }}>
                                            एकूण विकलेले नग (TOTAL ITEMS SOLD)
                                        </Typography>
                                        <FastfoodIcon sx={{ color: '#ea580c', fontSize: 26 }} />
                                    </Box>
                                    <Typography variant="h4" sx={{ fontWeight: 900, color: '#9a3412' }}>
                                        {totalItemsSoldQty} <Typography component="span" variant="subtitle2" sx={{ fontWeight: 800, color: '#c2410c' }}>ITEMS</Typography>
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <Card sx={{ borderRadius: 3, border: '2px solid #10b981', bgcolor: '#ecfdf5', boxShadow: 'none' }}>
                                <CardContent sx={{ p: 2.5 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#047857' }}>
                                            सर्वोत्तम खपणारा पदार्थ (TOP SELLING DISH)
                                        </Typography>
                                        <EmojiEventsIcon sx={{ color: '#10b981', fontSize: 26 }} />
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 900, color: '#065f46', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {topSellingDish ? (currentLanguage === 'mr' ? topSellingDish.nameMr : topSellingDish.name) : 'N/A'}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#059669', display: 'block' }}>
                                        {topSellingDish ? `एकूण ${topSellingDish.totalQty} नग विक्री (₹${topSellingDish.totalRevenue.toFixed(0)})` : '-'}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <Card sx={{ borderRadius: 3, border: '2px solid #0284c7', bgcolor: '#f0f9ff', boxShadow: 'none' }}>
                                <CardContent sx={{ p: 2.5 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#0369a1' }}>
                                            पदार्थांतून मिळालेली रक्कम (ITEM REVENUE)
                                        </Typography>
                                        <CurrencyRupeeIcon sx={{ color: '#0284c7', fontSize: 26 }} />
                                    </Box>
                                    <Typography variant="h4" sx={{ fontWeight: 900, color: '#0c4a6e' }}>
                                        ₹{totalItemRevenue.toFixed(0)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Detailed Item Sales Table */}
                    <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 900, color: '#0f172a', mb: 2 }}>
                                पदार्थनिहाय खपाचा सविस्तर तक्ता (Item-Wise Sales Register)
                            </Typography>

                            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2.5 }}>
                                <Table size="medium">
                                    <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 900 }}>पदार्थाचे नाव (Dish Name)</TableCell>
                                            <TableCell sx={{ fontWeight: 900 }}>कॅटेगिरी (Category)</TableCell>
                                            <TableCell sx={{ fontWeight: 900 }} align="right">दर (Price ₹)</TableCell>
                                            <TableCell sx={{ fontWeight: 900 }} align="right">विकलेले नग (Qty Sold)</TableCell>
                                            <TableCell sx={{ fontWeight: 900 }} align="right">एकूण कमाई (Total Revenue)</TableCell>
                                            <TableCell sx={{ fontWeight: 900 }} align="right">विक्री वाटा (Sales Share %)</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {itemSalesData.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center" sx={{ py: 6, color: '#94a3b8', fontWeight: 600 }}>
                                                    निवडलेल्या फिल्टरसाठी कोणताही डेटा उपलब्ध नाही. (No item sales data)
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            itemSalesData.map((item, idx) => {
                                                const sharePercent = totalItemRevenue > 0 ? ((item.totalRevenue / totalItemRevenue) * 100).toFixed(1) : '0';
                                                const isTop = idx === 0;

                                                return (
                                                    <TableRow key={item.id || idx} hover sx={{ bgcolor: isTop ? '#ecfdf5' : 'inherit' }}>
                                                        <TableCell sx={{ fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            {isTop && <EmojiEventsIcon sx={{ color: '#10b981', fontSize: 20 }} />}
                                                            {currentLanguage === 'mr' ? item.nameMr : item.name}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip 
                                                                label={item.category} 
                                                                size="small" 
                                                                sx={{ fontWeight: 800, fontSize: '0.7rem', bgcolor: '#f1f5f9', color: '#334155' }} 
                                                            />
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 700 }}>₹{item.price}</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 900, color: '#ea580c', fontSize: '1rem' }}>
                                                            {item.totalQty} नग
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 900, color: '#10b981', fontSize: '0.95rem' }}>
                                                            ₹{item.totalRevenue.toFixed(0)}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 800, color: '#0284c7' }}>
                                                            {sharePercent}%
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Box>
            )}

            {/* Daily Closing Summary Receipt Dialog */}
            <Dialog open={openClosingDialog} onClose={() => setOpenClosingDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
                <DialogTitle sx={{ fontWeight: 900, textAlign: 'center', bgcolor: '#ea580c', color: '#ffffff', py: 2 }}>
                    🖨️ Daily Closing Summary
                </DialogTitle>
                <DialogContent sx={{ p: 3, bgcolor: '#ffffff' }}>
                    <Box sx={{ textTransform: 'uppercase', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 900, textAlign: 'center', mb: 0.5 }}>
                            DINEXA POS DAILY CLOSING
                        </Typography>
                        <Typography variant="caption" display="block" align="center" sx={{ color: '#64748b', mb: 2 }}>
                            Date: {new Date().toLocaleDateString()} | Time: {new Date().toLocaleTimeString()}
                        </Typography>
                        <Divider sx={{ my: 1, borderStyle: 'dashed' }} />

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 0.8 }}>
                            <span>TOTAL BILLS SETTLED:</span>
                            <strong>{totalOrdersCount}</strong>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 0.8 }}>
                            <span>CASH COLLECTION:</span>
                            <strong>₹{cashSales.toFixed(2)}</strong>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 0.8 }}>
                            <span>ONLINE / UPI COLLECTION:</span>
                            <strong>₹{upiSales.toFixed(2)}</strong>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 0.8 }}>
                            <span>TOTAL DISCOUNTS GIVEN:</span>
                            <strong>-₹{totalDiscount.toFixed(2)}</strong>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 0.8 }}>
                            <span>GST TAX COLLECTED (5%):</span>
                            <strong>₹{totalTax.toFixed(2)}</strong>
                        </Box>
                        <Divider sx={{ my: 1, borderStyle: 'dashed' }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 1, fontSize: '1rem', fontWeight: 900 }}>
                            <span>GRAND TOTAL DAY SALES:</span>
                            <span>₹{totalSales.toFixed(2)}</span>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: '#f8fafc' }}>
                    <Button onClick={() => setOpenClosingDialog(false)} sx={{ fontWeight: 700, color: '#64748b' }}>
                        Close
                    </Button>
                    <Button 
                        variant="contained" 
                        onClick={handlePrintDailyClosing}
                        sx={{ bgcolor: '#ea580c', '&:hover': { bgcolor: '#c2410c' }, fontWeight: 900, borderRadius: 2 }}
                    >
                        🖨️ Print Receipt
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
